import {
  Component,
  computed,
  effect,
  HostBinding,
  inject,
  signal,
} from '@angular/core';
import { IconsComponent } from '@shared/components/icons/icon.component';
import {
  bootstrapCheck2All,
  bootstrapExclamationOctagonFill,
  bootstrapShieldFillCheck,
  bootstrapShieldFillX,
  bootstrapXCircle,
} from '@ng-icons/bootstrap-icons';
import {
  CustomButtonComponent,
  CustomModalComponent,
} from '@shared/components';
import { buttonConfig, ResponseError } from '@core/types/types';
import { TranslateModule } from '@ngx-translate/core';
import {
  ModalService,
  RouteWatcherService,
  ToastService,
} from '@core/services';
import { ClaimService } from '@core/services/http/claim.service';
import { Subscription } from 'rxjs';
import { SettlementService } from '@core/services/http/settlement.service';
import { SettlementCreatedModalContentComponent } from './components/settlement-created-modal-content/settlement-created-modal-content.component';
import { Router } from '@angular/router';
import { environment } from '@env/environment';

@Component({
  selector: 'create-settlement-button-component',
  templateUrl: 'create-settlement-button.component.html',
  imports: [CustomButtonComponent, TranslateModule],
})
export class CreateSettlementButtonComponent {
  @HostBinding('style.display') display = 'none';

  private readonly claimService = inject(ClaimService);
  private readonly settlementService = inject(SettlementService);

  private settlement$?: Subscription;

  private readonly signals = {
    voidReason: signal<string | undefined>(undefined),
    claim: this.claimService.claim,
    createSettlementInfo: this.settlementService.createSettlementInfo,
  };

  private readonly modalRefs = {
    createSettlement: 'createSettlement',
    settlementCreationProgress: 'settlementCreationProgress',
    confirmCreateSettlement: 'confirmCreateSettlement',
    settlementCreated: 'settlementCreated',
  };

  protected blockSettlementCreationProgressModalDismiss = true;

  protected configs = computed(
    () =>
      ({
        type: 'button',
        label: {
          key: 'app.pageId.settlement.title',
        },
        startContent: {
          content: IconsComponent,
          data: {
            icon: bootstrapCheck2All,
            size: 17,
          },
        },
        endContent: {
          content: IconsComponent,
          data: {
            icon: bootstrapCheck2All,
            size: 17,
          },
        },
        color: 'primary',
        animation: 'slide-in-right',
        action: this.openSettlementModal.bind(this),
      } as buttonConfig)
  );

  constructor(
    private readonly routeWatcher: RouteWatcherService,
    private readonly modalService: ModalService,
    private readonly toastService: ToastService,
    private readonly router: Router
  ) {
    effect(() => {
      const watchedRoutes = this.routeWatcher.getWatchedRoutes([
        'settlement',
        'consult',
      ]);

      if (this.signals.createSettlementInfo() != undefined) {
        this.display =
          watchedRoutes.includes('settlement') &&
          !watchedRoutes.includes('consult')
            ? ''
            : 'none';
      }
    });
  }

  openSettlementModal() {
    if (this.settlementService.settlement()) return;

    const createSettlementInfo = this.signals.createSettlementInfo()!;
    const initialSettlementInfo =
      this.settlementService.initialSettlementInfo()!;

    if (!createSettlementInfo.file) {
      return this.toastService.show({
        ref: 'noSettlementDocumentAttached',
        color: 'secondary',
        delay: 3000,
        message: {
          key: 'app.pageId.settlement.no-settlement-document-attached',
        },
        icon: bootstrapExclamationOctagonFill,
      });
    }

    if (
      !createSettlementInfo.numeroFactura ||
      !createSettlementInfo.fechaFactura
    ) {
      return this.toastService.show({
        ref: 'missingInvoiceInfo',
        color: 'tertiary',
        delay: 3000,
        message: {
          key: 'app.pageId.settlement.missing-invoice-info',
        },
        icon: bootstrapExclamationOctagonFill,
      });
    }

    if (
      !createSettlementInfo.numeroComprobanteFiscal ||
      !createSettlementInfo.fechaComprobanteFiscal ||
      !createSettlementInfo.fechaExpiracionComprobanteFiscal
    ) {
      return this.toastService.show({
        ref: 'missingTaxReceiptInfo',
        color: 'tertiary',
        delay: 3000,
        message: {
          key: 'app.pageId.settlement.missing-tax-info',
        },
        icon: bootstrapExclamationOctagonFill,
      });
    }

    if (
      createSettlementInfo.reclamos == undefined ||
      createSettlementInfo.reclamos.length <= 0
    ) {
      return this.toastService.show({
        ref: 'noClaimsToSettle',
        color: 'tertiary',
        delay: 3000,
        message: { key: 'app.pageId.settlement.no-claims-to-settle' },
        icon: bootstrapShieldFillX,
      });
    }

    if (initialSettlementInfo.incompletedClaimsCount > 0) {
      return this.openSettlementConfirmationModal();
    }

    this.createSettlement();
  }

  beforeModalDismiss() {
    return !this.blockSettlementCreationProgressModalDismiss;
  }

  createSettlement() {
    this.openProgressModal();
    this.settlement$ = this.settlementService
      .createSettlement(this.settlementService.initialSettlementInfo()!.token)
      .subscribe({
        next: (response) => {
          if (!response.data) {
            this.blockSettlementCreationProgressModalDismiss = false;

            this.modalService.dismiss(
              this.modalRefs.settlementCreationProgress
            );

            this.toastService.show({
              ref: 'settlementCreationError',
              color: 'tertiary',
              message: {
                key: 'error.generic',
                params: {
                  message: (response as any as ResponseError).error.message,
                  code: (response as any as ResponseError).error.error,
                },
              },
              icon: bootstrapShieldFillX,
            });

            throw response;
          }

          this.loadSettlementInfo(response.data.solicitudRadicacionId);
        },
        error: (error: ResponseError) => {
          this.blockSettlementCreationProgressModalDismiss = false;
          this.modalService.dismiss(this.modalRefs.settlementCreationProgress);

          this.toastService.show({
            ref: 'settlementCreationError',
            color: 'tertiary',
            message: {
              key: 'error.generic',
              params: { message: error.error.message, code: error.error.error },
            },
            icon: bootstrapXCircle,
          });
        },
      });
  }

  openProgressModal() {
    const modalConfig = () => ({
      type: 'progress',
      beforeDismiss: this.beforeModalDismiss.bind(this),
      title: `app.pageId.settlement.creating.title`,
      message: 'app.pageId.settlement.creating.description',
      size: 'sm',
      loader: {
        type: 'spinner',
        color: 'primary',
      },
    });

    this.modalService.open(
      CustomModalComponent,
      modalConfig as any,
      this.modalRefs.settlementCreationProgress
    );
  }

  ngOnDestroy(): void {
    this.settlement$?.unsubscribe();
  }

  openSettlementConfirmationModal() {
    const modalConfig = () => ({
      type: 'confirmation',
      title: 'app.pageId.settlement.confirm-modal.title',
      description: 'app.pageId.settlement.confirm-modal.description',
      size: 'sm',
      actions: {
        cancel: {
          type: 'button',
          label: {
            key: 'app.pageId.settlement.confirm-modal.cancel',
          },
          color: 'tertiary',
          theme: 'light',
          action: () => {
            this.modalService.dismiss(this.modalRefs.confirmCreateSettlement);
          },
        },
        confirm: {
          type: 'button',
          label: {
            key: 'app.pageId.settlement.confirm-modal.confirm',
          },
          color: 'primary',
          action: () => {
            this.modalService.dismiss(this.modalRefs.confirmCreateSettlement);
            this.createSettlement();
          },
        },
      },
    });

    this.modalService.open(
      CustomModalComponent,
      modalConfig as any,
      this.modalRefs.confirmCreateSettlement
    );
  }

  showSettlementCreatedModal() {
    const modalConfig = () => ({
      type: 'custom',
      beforeDismiss: () => false,
      size: 'md',
      body: {
        content: SettlementCreatedModalContentComponent,
      },
    });

    this.modalService.open(
      CustomModalComponent,
      modalConfig as any,
      this.modalRefs.settlementCreated
    );
  }

  openRedirectConfirmationModal(urlRedirect: string) {
    const modalConfig = () => ({
      type: 'confirmation',
      title: 'app.pageId.redirect.title',
      description: 'app.pageId.redirect.description',
      size: 'sm',
      actions: {
        cancel: {
          type: 'button',
          label: {
            key: 'app.pageId.redirect.cancel',
          },
          color: 'tertiary',
          theme: 'light',
          action: () => {
            this.modalService.dismiss('redirect');
          },
        },
        confirm: {
          type: 'button',
          label: {
            key: 'app.pageId.redirect.confirm',
          },
          color: 'primary',
          action: () => {
            let a = document.createElement('a') as HTMLAnchorElement;
            a.href = urlRedirect;
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            document.removeChild(a);
            this.modalService.dismiss('redirect');
          },
        },
      },
    });

    this.modalService.open(
      CustomModalComponent,
      modalConfig as any,
      'redirect'
    );
  }

  loadSettlementInfo(solicitudRadicacionId: number) {
    this.settlementService
      .getCreatedSettlementInfo(
        solicitudRadicacionId,
        this.settlementService.initialSettlementInfo()?.token!
      )
      .subscribe(
        (response) => {
          if (response.statusCode === 202) {
            setTimeout(() => {
              this.loadSettlementInfo(solicitudRadicacionId);
            }, environment.pollingInterval);
          } else {
            this.blockSettlementCreationProgressModalDismiss = false;
            this.modalService.dismiss(
              this.modalRefs.settlementCreationProgress
            );

            this.settlementService.setSettlement(response.data);
            this.router.navigate([
              'app',
              'settlement',
              'consult',
              solicitudRadicacionId,
            ]);

            if (response.data.statusReason) {
              return this.toastService.show({
                ref: 'settlementCreatedWithError',
                color: 'tertiary',
                message: { key: 'app.pageId.settlement-created-with-error' },
                icon: bootstrapShieldFillX,
              });
            }

            this.toastService.show({
              ref: 'settlementCreated',
              color: 'success',
              message: { key: 'app.pageId.settlement-created' },
              icon: bootstrapShieldFillCheck,
            });

            if (response.data.urlRedirect) {
              this.openRedirectConfirmationModal(response.data.urlRedirect);
            }
          }
        },
        (err: ResponseError) => {
          this.blockSettlementCreationProgressModalDismiss = false;
          this.modalService.dismiss(this.modalRefs.settlementCreationProgress);

          this.toastService.show({
            ref: 'settlementCreationError',
            color: 'tertiary',
            message: {
              key: 'error.generic',
              params: { message: err.error.message, code: err.error.error },
            },
            icon: bootstrapXCircle,
          });
        }
      );
  }
}
